/**
 * Stage Zero domain model.
 *
 * These types are the single source of truth for a project. They are plain,
 * serialisable data: no class instances, no AudioNodes, no React state, no
 * ArrayBuffers. Audio sample data lives in the storage layer and is referenced
 * by `AudioAssetRef.id`.
 *
 * Anything added here must survive `JSON.stringify` / `JSON.parse` unchanged,
 * because that round trip is exactly what project save/load performs.
 */

export const PROJECT_FORMAT_VERSION = 1 as const;

/** Pulses (ticks) per quarter note. Fixed for format v1. */
export const PPQ = 960;

// ---------------------------------------------------------------------------
// Transport / timing
// ---------------------------------------------------------------------------

export interface TempoEvent {
  /** Position on the musical timeline, in ticks. The first event is always 0. */
  tick: number;
  bpm: number;
  /**
   * When true the tempo moves from this event's BPM to the next event's
   * across the segment — an accelerando or ritardando — instead of holding and
   * jumping at the next event. Meaningless on the last event, which has
   * nothing to ramp towards.
   */
  ramp?: boolean;
  /**
   * How a ramp moves. `linear` (the default) adds the same BPM per beat;
   * `exponential` multiplies by the same ratio per beat, which is the shape a
   * ritardando that *sounds* even has — each beat a little longer than the
   * last by the same proportion, rather than by the same number of BPM.
   */
  rampShape?: TempoRampShape;
}

export const TEMPO_RAMP_SHAPES = ['linear', 'exponential'] as const;
export type TempoRampShape = (typeof TEMPO_RAMP_SHAPES)[number];

export interface TimeSignatureEvent {
  tick: number;
  numerator: number;
  /** Note value that gets the beat: 1, 2, 4, 8, 16... */
  denominator: number;
}

export type SnapValue =
  | 'off'
  | 'bar'
  | '1/1'
  | '1/2'
  | '1/4'
  | '1/8'
  | '1/16'
  | '1/32';

export interface TransportSettings {
  ppq: number;
  tempoMap: TempoEvent[];
  timeSignatures: TimeSignatureEvent[];
  loopEnabled: boolean;
  loopStartTicks: number;
  loopEndTicks: number;
  metronomeEnabled: boolean;
  /** Bars of count-in before recording starts. 0 disables count-in. */
  countInBars: number;
  snap: SnapValue;
  /**
   * Length used when a crossfade is created without one being given — at comp
   * boundaries, and by the crossfade action. Zero disables automatic
   * crossfading at comp edits.
   */
  crossfadeSec: number;
  /** Playhead position persisted with the project, in ticks. */
  playheadTicks: number;
}

export interface Marker {
  id: string;
  name: string;
  tick: number;
  color: string;
}

// ---------------------------------------------------------------------------
// Assets
// ---------------------------------------------------------------------------

/**
 * Metadata for an imported or recorded audio asset. The encoded bytes are held
 * by the storage layer under the same id; decoded AudioBuffers are cached in
 * memory by the AssetManager and are never serialised.
 */
export interface AudioAssetRef {
  id: string;
  name: string;
  /** Original file name, when the asset came from disk. */
  originalFileName: string | null;
  mimeType: string;
  byteLength: number;
  sampleRate: number;
  channelCount: number;
  durationSec: number;
  createdAt: number;
  /** True when the asset was captured from an input device rather than imported. */
  recorded: boolean;
}

// ---------------------------------------------------------------------------
// Clips
// ---------------------------------------------------------------------------

export type FadeCurve = 'linear' | 'equalPower' | 'exponential';

export interface ClipFade {
  /** Fade length in seconds of clip-local time. */
  durationSec: number;
  curve: FadeCurve;
  /**
   * How far the fade is bent away from its curve's natural shape, -1 to +1.
   *
   * Zero is the named law exactly. Positive bends the fade to rise late and
   * fast; negative to rise early and slowly. The shaping is symmetric, so a
   * crossfade keeps its constant-power property at any curvature — see
   * `docs/CROSSFADES.md`.
   */
  curvature: number;
}

interface ClipBase {
  id: string;
  trackId: string;
  name: string;
  /** Start position on the musical timeline, in ticks. */
  startTicks: number;
  muted: boolean;
  color: string | null;
  /**
   * Which lane of the track this clip lives on.
   *
   * Lane 0 is the *comp*: the material that actually sounds. Lanes 1 and above
   * hold alternate takes, kept for comping and never scheduled. Modelling takes
   * as ordinary clips on a lane index — rather than as a separate structure —
   * means every clip operation already works on them: they trim, split, move,
   * save, undo and travel in a project package with no special handling.
   */
  takeLane: number;
}

/**
 * A non-destructive reference to a region of an AudioAsset.
 *
 * `offsetSec` and `durationSec` describe the region of source audio consumed.
 * The clip's extent on the timeline is derived from `durationSec` through the
 * tempo map (see `timing/convert.ts`) — audio is never time-stretched in v1, so
 * the source region is the authoritative length.
 */
/**
 * One point of a clip's own gain envelope.
 *
 * Positioned in **source seconds** — a position in the asset, not on the
 * timeline — so the ride stays on the audio through trims, splits and stretches.
 * See `domain/clipGain.ts`.
 */
export interface ClipGainPoint {
  id: string;
  sourceSec: number;
  /** Linear gain. 1 is unity. */
  gain: number;
  curve: AutomationCurve;
}

export interface AudioClip extends ClipBase {
  kind: 'audio';
  assetId: string;
  offsetSec: number;
  durationSec: number;
  /** Linear gain applied to this clip only, before the track fader. */
  gain: number;
  /**
   * A level ride drawn inside the clip, applied before `gain` and the fades.
   * Empty means no envelope at all, which is not the same as a flat one: it is
   * one less thing to draw and one less thing to schedule.
   */
  gainEnvelope: ClipGainPoint[];
  fadeIn: ClipFade;
  fadeOut: ClipFade;
  /**
   * Playback rate multiplier. 1 = unmodified, 2 = plays twice as fast and takes
   * half as long on the timeline.
   */
  playbackRate: number;
  /**
   * How the rate is achieved. True stretches the audio (WSOLA) so the pitch is
   * unchanged; false resamples it, which is varispeed — a tape machine — and
   * moves the pitch with the speed. Ignored at a rate of 1.
   */
  preservePitch: boolean;
  /**
   * The tempo this clip's audio is fitted to the grid at, in BPM, when it
   * should follow the project tempo. Set, `playbackRate` is ignored and the
   * rate is derived from the tempo map instead, so changing the project tempo
   * refits the clip. Null means the clip plays at its own stored rate and
   * ignores the tempo.
   *
   * It starts as the tempo the audio was recorded at. Trimming a clip whose
   * markers have bent its curve refits it to whatever keeps the remaining beats
   * where they were, so after such a trim it is a fit rather than a fact. See
   * `sliceWarp`.
   */
  warpSourceBpm: number | null;
  /**
   * Points pinning a moment in this clip's source audio to a musical position
   * inside it. Between them the audio is stretched linearly, so a performance
   * that drifts against the grid can be pulled straight without moving the parts
   * of it that were already right. Only meaningful while the clip follows the
   * tempo; the clip's own start and end are always pinned and are not listed.
   */
  warpMarkers: WarpMarker[];
  reversed: boolean;
  /**
   * Transposition in semitones, positive up, fractional for cents. Independent
   * of `playbackRate`: the clip keeps the length the timeline gives it and the
   * audio is re-rendered at the new pitch. 0 leaves the recording untouched.
   *
   * It applies in varispeed too, where it stacks on the pitch the speed already
   * moved — a clip played at half speed and shifted up an octave sounds at its
   * original pitch, which is the only reading of the two controls that lets
   * either be adjusted without undoing the other.
   */
  pitchSemitones: number;
  /**
   * Where the formants sit, in semitones from where they were recorded.
   *
   * A pitch shift multiplies every frequency by the same factor, resonances
   * included, so a transposed voice takes on the character of a differently
   * sized singer. This says what should happen to them instead:
   *
   * - `null` — the default — lets them go wherever the pitch goes, which is what
   *   a shifter does if left alone, and what every clip transposed before this
   *   existed did.
   * - `0` holds them exactly where they were, so a transposed voice still sounds
   *   like the same singer.
   * - Any other number moves them on their own. `+5` with no pitch shift at all
   *   is the same note sung by a smaller person.
   *
   * Null rather than a number for "follow the pitch", because following is not a
   * position: writing it as "equal to `pitchSemitones`" would be true only until
   * the pitch was next touched.
   */
  formantSemitones: number | null;
}

export interface WarpMarker {
  id: string;
  /** Position in the clip's source region, in seconds from its start. */
  sourceSec: number;
  /** Where that moment lands, in quarter notes from the clip's start. */
  beat: number;
}

export interface MidiNote {
  id: string;
  /** MIDI note number, 0-127. */
  pitch: number;
  /** 1-127. */
  velocity: number;
  /** Offset from the clip start, in ticks. */
  startTicks: number;
  durationTicks: number;
  channel: number;
}

export interface MidiClip extends ClipBase {
  kind: 'midi';
  durationTicks: number;
  notes: MidiNote[];
  /** Loop the note content across the clip length. */
  loop: boolean;
  /** Length of one loop iteration, in ticks. Ignored when `loop` is false. */
  loopLengthTicks: number;
}

export type Clip = AudioClip | MidiClip;

export const isAudioClip = (clip: Clip): clip is AudioClip => clip.kind === 'audio';
export const isMidiClip = (clip: Clip): clip is MidiClip => clip.kind === 'midi';

// ---------------------------------------------------------------------------
// Effects and instruments
// ---------------------------------------------------------------------------

export type EffectType =
  | 'eq3'
  | 'compressor'
  | 'reverb'
  | 'delay'
  | 'filter'
  | 'limiter'
  | 'chorus'
  | 'saturator'
  | 'wam';

/**
 * One parameter of a hosted Web Audio Module, as the plugin described it.
 * Kept on the effect so a project is self-describing: the inspector can draw
 * the plugin's controls and a lane can be normalised without the plugin
 * loaded.
 */
export interface WamParameterInfo {
  id: string;
  label: string;
  min: number;
  max: number;
  default: number;
  units: string;
}

export interface EffectInstance {
  id: string;
  type: EffectType;
  enabled: boolean;
  /** Parameter values keyed by parameter id, as defined by the effect descriptor. */
  params: Record<string, number>;
  /**
   * For a reverb: the asset whose audio is its impulse response — a recording
   * of a real room, played through a convolver. Absent, the reverb uses its
   * synthesised tail. Only meaningful on a reverb; ignored elsewhere.
   */
  impulseAssetId?: string | null;
  /**
   * For a hosted Web Audio Module: the URL its code is fetched from — the one
   * thing in this application that reaches out of the machine, and only on a
   * user's say-so — with the name and parameters the plugin reported once it
   * loaded. Only meaningful on a `wam` insert.
   */
  wamUrl?: string;
  wamName?: string;
  wamParameters?: WamParameterInfo[];
  /**
   * The plugin's whole state as it reported it — a preset, a wavetable, a
   * sequence, whatever it keeps beyond its parameters — as JSON, handed back
   * when the plugin loads. Captured when the project is saved or packaged
   * and when the plugin's interface is closed.
   */
  wamState?: unknown;
}

export type InstrumentType = 'synth' | 'drumMachine' | 'sampler' | 'wam';

export interface InstrumentState {
  type: InstrumentType;
  params: Record<string, number>;
  /** Free-form, instrument-specific structured state (e.g. drum patterns). */
  data: Record<string, unknown>;
}

export interface Send {
  id: string;
  /** Track id of the aux/bus receiving this send. */
  targetTrackId: string;
  gain: number;
  preFader: boolean;
  enabled: boolean;
}

// ---------------------------------------------------------------------------
// Tracks
// ---------------------------------------------------------------------------

export type TrackKind = 'audio' | 'instrument' | 'bus' | 'aux' | 'master';

export interface Track {
  id: string;
  kind: TrackKind;
  name: string;
  color: string;
  /** Lane height in CSS pixels. */
  height: number;
  /** Linear gain of the channel fader. */
  volume: number;
  /** -1 (hard left) .. +1 (hard right). */
  pan: number;
  mute: boolean;
  solo: boolean;
  armed: boolean;
  inputMonitor: boolean;
  /** Selected input device id for recording, or null for the system default. */
  inputDeviceId: string | null;
  /** Destination track id. `null` means "route to master". Master routes nowhere. */
  outputTrackId: string | null;
  sends: Send[];
  effects: EffectInstance[];
  instrument: InstrumentState | null;
  clipIds: string[];
  automationLaneIds: string[];
  /** Whether moving a control on this track writes automation. */
  automationMode: AutomationMode;
  collapsed: boolean;
  /** Automation lanes shown under the track lane. */
  showAutomation: boolean;
  /** Take lanes shown under the track lane. */
  showTakeLanes: boolean;
  /**
   * What each take lane is called, keyed by lane number. Lanes without an entry
   * are called after their number. See `domain/takes.ts`.
   */
  takeNames: Record<string, string>;
  /** Rendered-down state, when the track has been frozen. See `domain/freeze.ts`. */
  freeze: TrackFreeze | null;
}

/**
 * A frozen track plays a pre-rendered file instead of running its clips,
 * instrument and effects. The material that produced it is untouched, so
 * unfreezing is just discarding this record.
 */
export interface TrackFreeze {
  /** The rendered audio, registered in `project.assets` like any other import. */
  assetId: string;
  /** Where the render starts on the timeline, and how long it runs. */
  startTicks: number;
  lengthTicks: number;
  /**
   * A fingerprint of everything the render depended on. When the track no
   * longer matches it, the freeze is stale and the UI says so rather than
   * quietly playing something that is no longer true.
   */
  signature: string;
  createdAt: number;
}

// ---------------------------------------------------------------------------
// Automation
// ---------------------------------------------------------------------------

export type AutomationTargetKind = 'track' | 'effect' | 'instrument';

export interface AutomationTarget {
  kind: AutomationTargetKind;
  trackId: string;
  /** Required when `kind === 'effect'`. */
  effectId: string | null;
  /** Parameter id: 'volume' | 'pan' for tracks, or an effect/instrument param id. */
  paramId: string;
}

export type AutomationCurve = 'linear' | 'hold' | 'smooth';

export interface AutomationPoint {
  id: string;
  tick: number;
  /** Normalised 0..1 value; mapped to the parameter range by its descriptor. */
  value: number;
  curve: AutomationCurve;
}

/**
 * What a track does with its automation while the transport runs.
 *
 * `read` plays the lanes and ignores the controls, which is what a mix does most
 * of the time. The other three write: `touch` only while a control is held,
 * `latch` from the first touch until the transport stops, and `write` from the
 * moment it rolls whether anything is touched or not.
 */
export type AutomationMode = 'read' | 'touch' | 'latch' | 'write';

export interface AutomationLane {
  id: string;
  target: AutomationTarget;
  enabled: boolean;
  points: AutomationPoint[];
  height: number;
}

// ---------------------------------------------------------------------------
// Project
// ---------------------------------------------------------------------------

export interface ProjectMeta {
  id: string;
  name: string;
  createdAt: number;
  modifiedAt: number;
  formatVersion: number;
  /** Version of the app that last wrote the project. Informational. */
  appVersion: string;
}

export interface ViewState {
  /** Horizontal zoom, in CSS pixels per quarter note. */
  pixelsPerBeat: number;
  scrollTicks: number;
  scrollTop: number;
  /**
   * Vertical zoom: a multiplier applied to every track's own height when the
   * lanes are laid out. A multiplier rather than a height so that heights the
   * user set by dragging a track's edge survive a change of scale.
   */
  trackHeightScale: number;
  /** Which secondary editor is open, if any. */
  bottomPanel: 'none' | 'mixer' | 'pianoRoll' | 'drumMachine' | 'inspector';
}

export interface Project {
  meta: ProjectMeta;
  transport: TransportSettings;
  tracks: Record<string, Track>;
  /** Display and routing order for non-master tracks. */
  trackOrder: string[];
  masterTrackId: string;
  clips: Record<string, Clip>;
  automation: Record<string, AutomationLane>;
  assets: Record<string, AudioAssetRef>;
  markers: Marker[];
  view: ViewState;
}
